import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebounce } from "@/hooks/useDebounce";
import { DestinationProductCard } from "./DestinationProductCard";
import { useDestinationProducts } from "../hooks/partner-destinations.hooks";
import { describeError } from "../utils/describeError";
import { CURATED_LIST_MAX_ITEMS } from "../utils/curatedLimit";
import type { PartnerDestinationProduct } from "@/types/partner-destination";

type CatalogSortOrder = "default" | "aiScore";

const CATALOG_PAGE_SIZE = 20;
// While a sync fills an empty cache, poll fast enough that the "N loaded"
// count visibly moves without hammering the backend.
const CATALOG_SYNCING_POLL_MS = 4000;

interface DestinationCatalogPanelProps {
  destinationId: string;
  curatedProductCodes: Set<string>;
  onAdd: (product: PartnerDestinationProduct) => void;
  /** The curated list is at its limit: Add is disabled until an item is removed. */
  isCuratedFull: boolean;
  canWrite: boolean;
  /** A "Sync" job (`POST .../sync`) is currently running for this city. */
  isSyncing: boolean;
  onTriggerSync: () => void;
}

export const DestinationCatalogPanel: React.FC<
  DestinationCatalogPanelProps
> = ({
  destinationId,
  curatedProductCodes,
  onAdd,
  isCuratedFull,
  canWrite,
  isSyncing,
  onTriggerSync,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<CatalogSortOrder>("default");
  const debouncedSearch = useDebounce(search, 400);
  const isSearchActive = debouncedSearch.trim().length > 0;

  // `curated: false` — the server already excludes items on the saved
  // curated list. Unsaved local additions aren't reflected here until the
  // next save, so `curatedProductCodes` below still guards against
  // re-adding one (the PUT rejects a duplicate product code).
  const { data, isLoading, isError, error } = useDestinationProducts(
    destinationId,
    {
      search: debouncedSearch || undefined,
      page,
      limit: CATALOG_PAGE_SIZE,
      curated: false,
    },
    { refetchInterval: isSyncing ? CATALOG_SYNCING_POLL_MS : false },
  );

  // Client-side sort of the current page only — there's no server-side sort
  // query param in this contract yet, so switching pages resets the order
  // to whatever that page's default (Viator) order is.
  const products = useMemo(() => {
    const rawProducts = data?.data ?? [];
    if (sortOrder !== "aiScore") return rawProducts;
    return [...rawProducts].sort((a, b) => {
      if (a.aiScore == null && b.aiScore == null) return 0;
      if (a.aiScore == null) return 1;
      if (b.aiScore == null) return -1;
      return b.aiScore - a.aiScore;
    });
  }, [data?.data, sortOrder]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / CATALOG_PAGE_SIZE));
  // Owner, 2026-09-28: a new city's cache is empty until the first Sync,
  // and nothing said so — it just looked like a broken, empty screen.
  const isCacheEmpty = !isSearchActive && total === 0;

  // Scoped to that exact case: a sync that started from an empty cache
  // keeps the "Loading activities…" banner up (with the count growing via
  // the poll above) until it finishes, instead of flipping to the normal
  // grid mid-fill or flashing the "press Sync" empty state part way
  // through. A re-sync of an already-populated cache shows the plain grid
  // updating underneath, as before.
  const wasEmptyAtSyncStartRef = useRef(false);
  const wasSyncingRef = useRef(false);
  if (isSyncing && !wasSyncingRef.current) {
    wasEmptyAtSyncStartRef.current = isCacheEmpty;
  }
  wasSyncingRef.current = isSyncing;
  const showSyncingFromEmptyBanner =
    isSyncing && wasEmptyAtSyncStartRef.current;

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-foreground text-lg font-semibold">
        {t("partners.admin.destinations.detail.catalogTitle")}
      </h2>

      <div className="flex items-center gap-2">
        <Input
          icon={<Search className="size-4" />}
          placeholder={t(
            "partners.admin.destinations.detail.catalogSearchPlaceholder",
          )}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="flex-1"
        />
        <Select
          value={sortOrder}
          onValueChange={(value) => setSortOrder(value as CatalogSortOrder)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">
              {t("partners.admin.destinations.detail.sortDefault")}
            </SelectItem>
            <SelectItem value="aiScore">
              {t("partners.admin.destinations.detail.sortByAiScore")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && (
        <p className="text-muted-foreground py-8 text-center text-sm">
          {t("partners.admin.destinations.loading")}
        </p>
      )}

      {isError && (
        <p className="text-destructive py-8 text-center text-sm">
          {describeError(
            t("partners.admin.destinations.detail.catalogLoadError"),
            error,
          )}
        </p>
      )}

      {!isLoading && !isError && showSyncingFromEmptyBanner && (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed p-6 text-center">
          <RefreshCw className="text-muted-foreground size-4 animate-spin" />
          <p className="text-muted-foreground text-sm">
            {t("partners.admin.destinations.detail.catalogSyncingFromViator", {
              count: total,
            })}
          </p>
        </div>
      )}

      {!isLoading &&
        !isError &&
        !showSyncingFromEmptyBanner &&
        isCacheEmpty && (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-6 text-center">
            <p className="text-muted-foreground text-sm">
              {t("partners.admin.destinations.detail.catalogEmptyTitle")}
              <br />
              {t("partners.admin.destinations.detail.catalogEmptyHint")}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canWrite}
              title={
                canWrite
                  ? undefined
                  : t(
                      "common.insufficientPermissions",
                      "Insufficient permissions",
                    )
              }
              onClick={onTriggerSync}
            >
              <RefreshCw className="size-4" />
              {t("partners.admin.destinations.sync")}
            </Button>
          </div>
        )}

      {!isLoading &&
        !isError &&
        !showSyncingFromEmptyBanner &&
        !isCacheEmpty &&
        products.length === 0 && (
          <p className="text-muted-foreground py-8 text-center text-sm">
            {t("partners.admin.destinations.detail.catalogEmpty")}
          </p>
        )}

      {!isLoading && !isError && products.length > 0 && (
        <div className="flex flex-col gap-2">
          {products.map((product) => {
            const isCurated = curatedProductCodes.has(product.productCode);
            return (
              <DestinationProductCard
                key={product.productCode}
                product={product}
                action={
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isCurated || isCuratedFull || !canWrite}
                    title={
                      !isCurated && isCuratedFull
                        ? t(
                            "partners.admin.destinations.detail.curatedLimitReached",
                            { max: CURATED_LIST_MAX_ITEMS },
                          )
                        : undefined
                    }
                    onClick={() => onAdd(product)}
                  >
                    <Plus className="size-4" />
                    {isCurated
                      ? t("partners.admin.destinations.curatedOn")
                      : t("partners.admin.destinations.detail.add")}
                  </Button>
                }
              />
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            {t("partners.admin.destinations.detail.previous")}
          </Button>
          <span className="text-muted-foreground text-xs">
            {t("partners.admin.destinations.detail.pageOf", {
              page,
              totalPages,
            })}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() =>
              setPage((current) => Math.min(totalPages, current + 1))
            }
          >
            {t("partners.admin.destinations.detail.next")}
          </Button>
        </div>
      )}
    </div>
  );
};
